
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import { get, isFunction, isNumber } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import { SlidesStore } from '@/shared-utils/slides-store';

interface SlidesConfig {
    useMetadata?: boolean;
    url: string;
    getSlideImageUrl: (slideshowDocId: string, slideIndex: number) => Promise<string>;
}

export default Vue.extend({
    name: 'Slides',

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<SlidesConfig>,
            default: () => ({} as SlidesConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        const url = this.config.url;
        const slidesStore = url ? new SlidesStore(url) : null;

        return {
            imageSrc: null as string | null,
            slidesStore,
            slideshowDocId: null as string | null,
            slideIndex: null as number | null,
            unsubscribers: [] as Unsubscriber[],
            getSlideImageUrl: this.config.getSlideImageUrl
        };
    },

    computed: {
        useMetadata(): boolean {
            return this.config?.useMetadata ?? true;
        }
    },

    created(): void {
        console.log('[SlidesComponent] created');

        this.unsubscribers.push(
            this.api.onMetadataChange((payload) => this.onMetadataChange(payload)),
            this.api.onClientAction(this.onClientAction.bind(this))
        );
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    methods: {
        onMetadataChange(payload: unknown): void {
            if (!this.useMetadata) return;

            const slide = get(payload, 'value.slide', {});

            this.slideshowDocId = get(slide, 'slideshowDocId');
            this.slideIndex = get(slide, 'slideIndex');

            this.loadImage();
        },

        onClientAction(payload: unknown, context?: string) {
            if (context !== 'slide') return;
            if (this.useMetadata) return;

            this.slideshowDocId = get(payload, 'slideshowDocId');
            this.slideIndex = get(payload, 'slideIndex');

            this.loadImage();
        },

        async loadImage() {
            let newSrc = null;
            if (this.slideshowDocId && isNumber(this.slideIndex)) {
                if (this.getSlideImageUrl) {
                    newSrc = await this.getSlideImageUrl(this.slideshowDocId, this.slideIndex);
                } else if (this.slidesStore) {
                    newSrc = await this.slidesStore.getImage(this.slideshowDocId, this.slideIndex).catch(() => {
                        return null;
                    });
                }
            }

            this.imageSrc = newSrc;

            if (!newSrc) {
                this.emitHasContent(false);
            }
        },

        emitHasContent(hasContent: boolean) {
            console.log('[SlidesComponent] emitHasContent', hasContent);
            this.$emit('has-content', hasContent);
        },

        onImageLoad() {
            this.emitHasContent(true);
        },

        onImageError() {
            this.emitHasContent(false);
        }
    }
});
